import { FilterPills, type PillItem } from '@/components/FilterPills';
import { useSetSearchParams } from '@/lib/searchParams';
import { spacePillLabel } from '@/lib/spaceLabels';
import { spacesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { type SpaceId, SpaceIdSchema } from '@rock/types';
import { type FC, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Space pills counting the tab's own rows: types per space on Catalog, candidates per space on Candidates. */
export const SpaceFilterPills: FC<{ rows: Array<{ spaceId: SpaceId }> }> = ({ rows }) => {
    const spaces = useAsyncAtomValue(spacesState, []);
    const spaceFilter = useSpaceFilter();
    const setFilters = useSetSearchParams();
    const pills: PillItem[] = useMemo(
        () =>
            spaces.map((space) => ({
                key: space.id,
                label: spacePillLabel(space),
                count: rows.filter((row) => row.spaceId === space.id).length,
            })),
        [spaces, rows],
    );

    return (
        <div className="rounded border bg-background p-3">
            <FilterPills
                label="Space"
                items={pills}
                selected={spaceFilter}
                allCount={rows.length}
                onSelect={(key) => setFilters({ space: key })}
            />
        </div>
    );
};

/** The rows in the space the pills picked, or all of them under "All". */
export function useSpaceFilteredRows<T extends { spaceId: SpaceId }>(rows: T[]): T[] {
    const spaceFilter = useSpaceFilter();
    return useMemo(() => (spaceFilter ? rows.filter((row) => row.spaceId === spaceFilter) : rows), [rows, spaceFilter]);
}

/**
 * The `?space=` filter, in the URL like the models/brands/library lists: it survives a refresh and the
 * row → detail → back round trip.
 */
export function useSpaceFilter(): SpaceId | undefined {
    const [searchParams] = useSearchParams();
    const space = searchParams.get('space');
    return space ? SpaceIdSchema.parse(space) : undefined;
}
